import store from '@/store';
const _store = store();

// Helper function to check feature licenses
const checkLicense = (feature) =>
  _store.getters['users/getFeatureLicense'](feature);

const routes = [
  {
    path: '/',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        component: () => import('src/pages/UserDashboardPage.vue'),
        name: 'dashboard',
        meta: {
          requiresAuth: true,
          forceUserCheck: true,
        },
        redirect: () => {
          if (sessionStorage.getItem('token')) {
            // Check if workspaces feature is licensed
            if (checkLicense('workspaces')) {
              return '/workspaces';
            } else {
              // If not licensed, redirect to scan page
              return '/search';
            }
          } else {
            return '/login';
          }
        },
        children: [
          {
            path: 'workspaces',
            component: () =>
              import(
                'src/components/UserDashboard/Workspaces/WorkspacesLayout.vue'
              ),
            beforeEnter: (to, from, next) => {
              // First check if the user has the workspaces license
              if (!checkLicense('workspaces')) {
                // If not licensed, redirect to scan page
                next('/scan');
                return;
              }

              const activeWorkspace =
                _store.getters['workspaces/activeWorkspace'];
              // Check if the route does not contain the currently active workspace ID
              if (activeWorkspace && !to.params.id) {
                // Redirect to the active workspace
                next({
                  name: 'workspaces.workspaceDetails',
                  params: { id: activeWorkspace.workspace_id },
                });
              } else {
                // Otherwise, continue
                next();
              }
            },
            children: [
              {
                path: '',
                name: 'workspaces.workspaceDefault',
                component: () =>
                  import(
                    'src/components/UserDashboard/Workspaces/WorkspaceDetailsMain.vue'
                  ), // Handles no ID, i.e. user has no workspaces
              },
              {
                path: ':id',
                name: 'workspaces.workspaceDetails',
                component: () =>
                  import(
                    'src/components/UserDashboard/Workspaces/WorkspaceDetailsMain.vue'
                  ), // Handles ID
              },
            ],
          },
          {
            path: 'scan',
            component: () =>
              import('src/components/UserDashboard/Scans/ScanHistory.vue'),
            name: 'scans',
          },
        ],
      },
      {
        path: 'search',
        component: () => import('src/pages/CreateScanPage.vue'),
        name: 'create-scan',
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'area52',
        component: () => import('src/pages/Area52Page.vue'),
        name: 'area52',
        beforeEnter(to, from, next) {
          if (checkLicense('area52')) {
            next();
          } else {
            next({
              name: from.name || 'dashboard',
            });
          }
        },
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'area52/app',
        component: () => import('src/pages/Area52AppPage.vue'),
        name: 'area52-app',
        meta: {
          requiresAuth: true,
        },
        props: true,
        children: [
          {
            path: 'bluechips',
            component: () => import('src/components/Area52/BlueChipsMain.vue'),
            name: 'bluechips-main',
          },
          {
            path: 'bluelens',
            component: () =>
              import('src/components/Area52/BlueLens/BlueLensMain.vue'),
            name: 'bluelens-main',
          },
        ],
      },
      {
        path: 'edit/scan/:id',
        component: () => import('src/pages/CreateScanPage.vue'),
        name: 'edit-scan',
        meta: {
          requiresAuth: true,
          forceUserCheck: true,
        },
      },
      {
        path: 'scan/:id/active/:active',
        component: () => import('src/pages/ScanDetailsPage.vue'),
        name: 'scan-details',
        meta: {
          requiresAuth: true,
        },
        props: true,
        children: [
          {
            path: 'publications',
            component: () =>
              import(
                'src/components/ScanComponents/Publications/PublicationsMain.vue'
              ),
            name: 'publications',
            redirect: (to) => {
              // Get the active view from the store
              const activeView = _store.getters['app/activeView'].publication;
              // Redirect to the child route equal to the active view
              return {
                name: `${to.name}.${activeView}`,
              };
            },
            meta: {
              dataSource: 'publication',
              requiresAuth: true,
            },
            children: [
              {
                path: 'documents',
                component: () =>
                  import(
                    'src/components/ScanComponents/Documents/DocumentsMain.vue'
                  ),
                name: 'publications.documents',
                meta: {
                  dataSource: 'publication',
                  requiresAuth: true,
                  forceUserCheck: true,
                },
              },
              {
                path: 'leaders',
                component: () =>
                  import(
                    'src/components/ScanComponents/Leadership/LeadershipMain.vue'
                  ),
                name: 'publications.leaders',
                meta: {
                  dataSource: 'publication',
                  requiresAuth: true,
                  forceUserCheck: true,
                },
              },
              {
                path: 'partnerships',
                component: () =>
                  import(
                    'src/components/ScanComponents/Partnerships/PartnershipsMain.vue'
                  ),
                name: 'publications.partnerships',
                meta: {
                  dataSource: 'publication',
                  requiresAuth: true,
                  forceUserCheck: true,
                },
              },
              {
                path: 'benchmarks',
                component: () =>
                  import(
                    'src/components/ScanComponents/Benchmarks/BenchmarksMain.vue'
                  ),
                name: 'publications.benchmarks',
                meta: {
                  dataSource: 'publication',
                  requiresAuth: true,
                  forceUserCheck: true,
                },
                beforeEnter(to, from, next) {
                  if (checkLicense('benchmarks')) {
                    next();
                  } else {
                    next({
                      name: from.name || 'dashboard',
                    });
                  }
                },
              },
              {
                path: 'emerging-trends',
                component: () =>
                  import('src/components/ScanComponents/Trends/TrendsMain.vue'),
                name: 'publications.emerging-trends',
                meta: {
                  dataSource: 'publication',
                  requiresAuth: true,
                  forceUserCheck: true,
                },
              },
            ],
          },
          {
            path: 'patents',
            component: () =>
              import('src/components/ScanComponents/Patents/PatentsMain.vue'),
            name: 'patents',
            redirect: (to) => {
              // Get the active view from the store
              const activeView = _store.getters['app/activeView'].patent;
              // Redirect to the child route equal to the active view
              return {
                name: `${to.name}.${activeView}`,
              };
            },
            meta: {
              dataSource: 'patent',
              requiresAuth: true,
            },
            children: [
              {
                path: 'documents',
                component: () =>
                  import(
                    'src/components/ScanComponents/Documents/DocumentsMain.vue'
                  ),
                name: 'patents.documents',
                meta: {
                  dataSource: 'patent',
                  requiresAuth: true,
                  forceUserCheck: true,
                },
              },
              {
                path: 'leaders',
                component: () =>
                  import(
                    'src/components/ScanComponents/Leadership/LeadershipMain.vue'
                  ),
                name: 'patents.leaders',
                meta: {
                  dataSource: 'patent',
                  requiresAuth: true,
                  forceUserCheck: true,
                },
              },
              {
                path: 'partnerships',
                component: () =>
                  import(
                    'src/components/ScanComponents/Partnerships/PartnershipsMain.vue'
                  ),
                name: 'patents.partnerships',
                meta: {
                  dataSource: 'patent',
                  requiresAuth: true,
                  forceUserCheck: true,
                },
              },
              {
                path: 'benchmarks',
                component: () =>
                  import(
                    'src/components/ScanComponents/Benchmarks/BenchmarksMain.vue'
                  ),
                name: 'patents.benchmarks',
                meta: {
                  dataSource: 'patent',
                  requiresAuth: true,
                  forceUserCheck: true,
                },
                beforeEnter(to, from, next) {
                  if (checkLicense('benchmarks')) {
                    next();
                  } else {
                    next({
                      name: from.name,
                    });
                  }
                },
              },
              {
                path: 'emerging-trends',
                component: () =>
                  import('src/components/ScanComponents/Trends/TrendsMain.vue'),
                name: 'patents.emerging-trends',
                meta: {
                  dataSource: 'patent',
                  requiresAuth: true,
                  forceUserCheck: true,
                },
              },
            ],
          },
          {
            path: 'funding',
            name: 'funding',
            redirect: (to) => {
              // Get the active view from the store
              const activeView = _store.getters['app/activeView'].funding;
              // Redirect to the child route equal to the active view
              return {
                name: `${to.name}.${activeView}`,
              };
            },
            meta: {
              dataSource: 'funding',
              requiresAuth: true,
              forceUserCheck: true,
            },
            component: () =>
              import('src/components/ScanComponents/Funding/FundingMain.vue'),
            children: [
              {
                path: 'dollars',
                name: 'funding.dollars',
                component: () =>
                  import(
                    'src/components/ScanComponents/Funding/FundingDollars.vue'
                  ),
                meta: {
                  dataSource: 'funding',
                  requiresAuth: true,
                  forceUserCheck: true,
                },
              },
              {
                path: 'acknowledgements',
                name: 'funding.acknowledgements',
                component: () =>
                  import(
                    'src/components/ScanComponents/Funding/FundingAcknowledgements.vue'
                  ),
                meta: {
                  dataSource: 'fundingPublication',
                  requiresAuth: true,
                  forceUserCheck: true,
                },
              },
            ],
          },
          {
            path: 'authors',
            name: 'authors',
            meta: {
              dataSource: 'author',
              requiresAuth: true,
              forceUserCheck: true,
            },
            component: () =>
              import('src/components/ScanComponents/Authors/AuthorsMain.vue'),
          },
          {
            path: 'funders',
            name: 'funders',
            meta: {
              dataSource: 'funder',
              requiresAuth: true,
              forceUserCheck: true,
            },
            component: () =>
              import('src/components/ScanComponents/Funders/FundersMain.vue'),
          },
          {
            path: 'institutions',
            name: 'institutions',
            meta: {
              dataSource: 'institution',
              requiresAuth: true,
              forceUserCheck: true,
            },
            component: () =>
              import(
                'src/components/ScanComponents/Institutions/InstitutionsMain.vue'
              ),
          },
          {
            path: 'inventors',
            name: 'inventors',
            meta: {
              dataSource: 'inventor',
              requiresAuth: true,
              forceUserCheck: true,
            },
            component: () =>
              import(
                'src/components/ScanComponents/Inventors/InventorsMain.vue'
              ),
          },
          {
            path: 'assignees',
            name: 'assignees',
            meta: {
              dataSource: 'assignee',
              requiresAuth: true,
              forceUserCheck: true,
            },
            component: () =>
              import(
                'src/components/ScanComponents/Assignees/AssigneesMain.vue'
              ),
          },
          {
            path: 'others',
            name: 'others',
            meta: {
              dataSource: 'bplEntities',
              requiresAuth: true,
              forceUserCheck: true,
            },
            component: () =>
              import(
                'src/components/ScanComponents/OtherEntities/OtherEntitiesMain.vue'
              ),
          },
        ],
      },
      {
        path: 'settings/profile',
        component: () => import('src/pages/UserProfilePage.vue'),
        name: 'profile',
        meta: {
          requiresAuth: true,
          forceUserCheck: true,
        },
      },
      {
        path: 'settings/admin',
        component: () => import('components/UserProfile/AdminTab.vue'),
        name: 'admin',
        meta: {
          requiresAuth: true,
          forceUserCheck: true,
        },
      },
      {
        path: 'user/password/reset/magic_key/:token',
        component: () => import('src/pages/ResetPasswordPage.vue'),
        name: 'password',
      },
      {
        path: 'login',
        component: () => import('pages/LoginPage.vue'),
        name: 'login',
      },
      {
        path: 'register/:auth?/:token?',
        component: () => import('src/pages/RegisterPage.vue'),
        name: 'register',
      },
      {
        path: 'about',
        component: () => import('src/pages/AboutPage.vue'),
        name: 'about',
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'help',
        component: () => import('src/pages/HelpPage.vue'),
        name: 'help',
        meta: {
          requiresAuth: true,
          forceUserCheck: true,
        },
      },
      {
        path: 'feedback',
        component: () => import('src/pages/FeedbackPage.vue'),
        name: 'feedback',
        meta: {
          requiresAuth: true,
          forceUserCheck: true,
        },
      },
      {
        path: 'ask-an-analyst',
        component: () => import('src/pages/AskAnAnalystPage.vue'),
        name: 'ask-an-analyst',
        meta: {
          requiresAuth: true,
          forceUserCheck: true,
        },
      },
      {
        path: 'release-notes',
        component: () => import('src/pages/ReleaseNotesPage.vue'),
        name: 'release-notes',
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'data-sources',
        component: () => import('src/pages/DataSourcesPage.vue'),
        name: 'data-sources',
        meta: {
          requiresAuth: true,
          forceUserCheck: true,
        },
      },
    ],
  },
];

// Always leave this as last one
if (process.env.MODE !== 'ssr') {
  routes.push({
    path: '*',
    component: () => import('src/pages/Error404Page.vue'),
  });
}

export default routes;
